import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Calendar, ChevronLeft, ChevronRight, RefreshCw, Users, Briefcase, MapPin, Clock } from 'lucide-react';
import { getJobCards } from '../../../../models/externalModel';
import { getAssignments, getFieldWorkers } from '../../../../models/mmsModel';

const MMSCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [loading, setLoading] = useState(true);
  const [jobCards, setJobCards] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetailsModal, setDayDetailsModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get start and end of current month
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const [jobCardsData, assignmentsData, workersData] = await Promise.all([
        getJobCards({
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: endOfMonth.toISOString().split('T')[0]
        }).catch(() => []),
        getAssignments({
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: endOfMonth.toISOString().split('T')[0]
        }).catch(() => ({ data: [] })),
        getFieldWorkers({ limit: 100 }).catch(() => ({ data: [] }))
      ]);

      setJobCards(Array.isArray(jobCardsData) ? jobCardsData : jobCardsData?.data || []);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : assignmentsData?.data || []);
      setWorkers(Array.isArray(workersData) ? workersData : workersData?.data || []);
    } catch (err) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return { jobs: [], assignments: [] };
    
    const dateStr = date.toISOString().split('T')[0];
    
    const dayJobs = jobCards.filter(job => {
      const jobDate = job.ISSUED_DATETIME ? new Date(job.ISSUED_DATETIME).toISOString().split('T')[0] : null;
      return jobDate === dateStr;
    });

    const dayAssignments = assignments.filter(assignment => {
      const assignDate = assignment.assigned_at ? new Date(assignment.assigned_at).toISOString().split('T')[0] : null;
      return assignDate === dateStr;
    });

    return { jobs: dayJobs, assignments: dayAssignments };
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const today = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  };

  const handleDayClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setDayDetailsModal(true);
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <Calendar className="h-5 w-5" style={{ color: '#0087D4' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">MMS Calendar</h1>
                <p className="text-xs text-gray-500 mb-1">
                  Job assignments and scheduling
                </p>
                <div className="flex items-center mt-1 gap-3">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {jobCards.length} job{jobCards.length !== 1 ? 's' : ''} this month
                  </span>
                  <span className="text-xs font-medium text-green-600">
                    {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} style={{ color: loading ? '#0096DE' : 'currentColor' }} />
            </button>
          </div>
        </div>

        {/* Calendar Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Previous Month"
              >
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={today}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Today
              </button>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Next Month"
              >
                <ChevronRight className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            <h2 className="text-lg font-semibold text-gray-800">
              {formatMonthYear(currentDate)}
            </h2>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                <span>Jobs</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span>Assigned</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
              <p className="text-gray-600">Loading calendar...</p>
            </div>
          ) : (
            <>
              {/* Week Day Headers */}
              <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
                {weekDays.map((day) => (
                  <div key={day} className="p-2 text-center text-sm font-semibold text-gray-700">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {days.map((date, index) => {
                  const events = date ? getEventsForDate(date) : { jobs: [], assignments: [] };
                  const hasEvents = events.jobs.length > 0 || events.assignments.length > 0;
                  const isTodayDate = isToday(date);

                  return (
                    <div
                      key={index}
                      onClick={() => handleDayClick(date)}
                      className={`min-h-24 p-2 border-b border-r border-gray-200 ${
                        date ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50'
                      } ${isTodayDate ? 'bg-blue-50' : ''}`}
                    >
                      {date && (
                        <>
                          <div className={`text-sm font-medium mb-1 ${
                            isTodayDate ? 'text-blue-600 font-bold' : 'text-gray-700'
                          }`}>
                            {date.getDate()}
                          </div>
                          
                          {hasEvents && (
                            <div className="space-y-1">
                              {events.jobs.length > 0 && (
                                <div className="flex items-center gap-1 text-xs">
                                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                  <span className="text-gray-600">{events.jobs.length} job{events.jobs.length > 1 ? 's' : ''}</span>
                                </div>
                              )}
                              {events.assignments.length > 0 && (
                                <div className="flex items-center gap-1 text-xs">
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  <span className="text-gray-600">{events.assignments.length} assigned</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-800">{jobCards.length}</div>
                <div className="text-xs text-gray-500">Total Jobs</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-800">{assignments.length}</div>
                <div className="text-xs text-gray-500">Assignments</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-800">{Array.isArray(workers) ? workers.filter(w => w.worker_status === '1').length : 0}</div>
                <div className="text-xs text-gray-500">Active Workers</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Day Details Modal */}
      {dayDetailsModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {selectedDate.toLocaleDateString('en-ZA', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {(() => {
                      const events = getEventsForDate(selectedDate);
                      return `${events.jobs.length} job${events.jobs.length !== 1 ? 's' : ''}, ${events.assignments.length} assignment${events.assignments.length !== 1 ? 's' : ''}`;
                    })()}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setDayDetailsModal(false);
                    setSelectedDate(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              {(() => {
                const events = getEventsForDate(selectedDate);
                
                if (events.jobs.length === 0 && events.assignments.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No jobs or assignments for this day</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    {/* Jobs Section */}
                    {events.jobs.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <Briefcase className="h-5 w-5 text-blue-600" />
                          Job Cards ({events.jobs.length})
                        </h3>
                        <div className="space-y-2">
                          {events.jobs.map((job, index) => (
                            <div key={index} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="font-semibold text-blue-900 mb-1">{job.JOBCARD_NO}</div>
                              <div className="text-sm text-gray-700 mb-2">{job.JOB_DESCRIPTION || job.APPLICATION_DESCRIPTION}</div>
                              <div className="flex items-center gap-4 text-xs text-gray-600">
                                {job.CUSTOMER_NAME && (
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    <span>{job.CUSTOMER_NAME}</span>
                                  </div>
                                )}
                                {job.STAND_NO && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    <span>{job.STAND_NO}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Assignments Section */}
                    {events.assignments.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <Users className="h-5 w-5 text-green-600" />
                          Assignments ({events.assignments.length})
                        </h3>
                        <div className="space-y-2">
                          {events.assignments.map((assignment, index) => {
                            const worker = Array.isArray(workers) ? workers.find(w => w.worker_id === assignment.worker_id) : null;
                            return (
                              <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="font-semibold text-green-900">{assignment.jobcard_no}</div>
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    assignment.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    assignment.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {assignment.status}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                                  <Users className="h-4 w-4" />
                                  <span>{worker?.worker_name || 'Unknown Worker'}</span>
                                </div>
                                {assignment.notes && (
                                  <div className="text-xs text-gray-600 mt-2 italic">{assignment.notes}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MMSCalendar;
